// IM-XPR-006 (IM-UX-132): aceite de convite pelo link. Quem aceita entra na MESMA organização dos colegas e passa a ver
// a base comercial dela. IM-SAAS-003 (IM-UX-178): o link mostra a organização e o perfil; quem já tem conta
// entra e aceita; quem não tem cria a conta aqui mesmo e entra direto na organização do convite (sem criar empresa).
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ROLE_NAME, type InvitationPreviewDto } from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { useToast } from '../components/Toast';
import { organizationAdapter } from '../data/adapters';
import { tenantAdminAdapter } from '../data/organizationAdmin';
import { friendlyError } from '../lib/errors';

const INVALID = 'Convite inválido, expirado ou já utilizado. Peça um novo convite ao administrador da organização.';

export function AcceptInvitation() {
  const { token = '' } = useParams();
  const { status, me, replaceMe, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<InvitationPreviewDto | null | 'invalid'>(null);
  const [mode, setMode] = useState<'choose' | 'signup'>('choose');
  useEffect(() => {
    let alive = true;
    tenantAdminAdapter.previewInvitation(token).then((p) => alive && setPreview(p)).catch(() => alive && setPreview('invalid'));
    return () => { alive = false; };
  }, [token]);
  if (status === 'loading') return <div className="sr-only" role="status">Carregando sessão…</div>;
  const welcome = (name: string | undefined) => toast(`Você entrou em ${name ?? 'sua organização'}. Empresas, pesquisas e decisões da equipe já estão disponíveis.`);
  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await organizationAdapter.accept({ token });
      replaceMe(r.me);
      welcome(r.me.activeOrganization?.name);
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyError(err, 'Não foi possível aceitar o convite agora. Tente novamente.'));
    } finally {
      setBusy(false);
    }
  };
  const signup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const name = String(f.get('name') ?? '').trim();
    const email = String(f.get('email') ?? '').trim();
    const password = String(f.get('password') ?? '');
    if (name.length < 2) { setError('Informe seu nome.'); return; }
    if (!email.includes('@')) { setError('Informe o e-mail que recebeu o convite.'); return; }
    if (password.length < 10) { setError('A senha precisa ter ao menos 10 caracteres.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await tenantAdminAdapter.signupWithInvitation({ token, name, email, password });
      replaceMe(r.me);
      welcome(r.me.activeOrganization?.name);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && [400, 404, 409].includes(err.status) && err.message ? err.message : friendlyError(err, 'Não foi possível criar a conta agora.'));
      setBusy(false);
    }
  };
  const p = preview && preview !== 'invalid' ? preview : null;
  return (
    <main className="onboarding">
      <BrandLogo variant="public" />
      <section className="card source-form" aria-labelledby="invite-title">
        <h1 id="invite-title" className="card-title">{p ? `Convite para ${p.organizationName}` : 'Convite para entrar na organização'}</h1>
        {preview === 'invalid' && <div className="form-error" role="alert">{INVALID}</div>}
        {p && <p className="card-sub">Perfil: <b>{ROLE_NAME[p.role]}</b> · destinado a <b>{p.emailHint}</b> · válido até {new Date(p.expiresAt).toLocaleDateString('pt-BR')}.</p>}
        <p className="card-sub">Ao aceitar, você passa a trabalhar na mesma base da sua equipe: empresas-alvo, pesquisas, battlecards e decisões são da organização, não de cada usuário.</p>
        {error && <div className="form-error" role="alert">{error}</div>}
        {status === 'authenticated' ? (
          <>
            <p className="card-sub">Você está conectado como <b>{me?.user.email}</b>. O convite só vale para o e-mail convidado.</p>
            <div className="form-actions">
              <button type="button" className="secondary" onClick={logout}>Entrar com outro e-mail</button>
              {me?.organizations.length ? <Link className="secondary btn-link" to="/">Agora não</Link> : null}
              <button type="button" className="cta" disabled={busy || preview === 'invalid'} onClick={accept}>{busy ? 'Entrando…' : 'Aceitar convite'}</button>
            </div>
          </>
        ) : preview === 'invalid' ? (
          <div className="form-actions"><Link className="cta" to="/login">Ir para o acesso</Link></div>
        ) : mode === 'choose' ? (
          <div className="form-actions">
            <button type="button" className="secondary" onClick={() => navigate('/login', { state: { from: `/convite/${token}` } })}>Já tenho conta</button>
            <button type="button" className="cta" onClick={() => { setMode('signup'); setError(null); }}>Criar conta e aceitar</button>
          </div>
        ) : (
          <form className="form" onSubmit={signup} noValidate aria-label="Criar conta e aceitar o convite">
            <div className="field"><label htmlFor="inv-name">Nome</label><input id="inv-name" name="name" maxLength={120} autoComplete="name" aria-required="true" /></div>
            <div className="field"><label htmlFor="inv-email">E-mail convidado</label><input id="inv-email" name="email" type="email" maxLength={254} autoComplete="email" aria-required="true" placeholder={p?.emailHint} /></div>
            <div className="field"><label htmlFor="inv-password">Senha (mín. 10 caracteres)</label><input id="inv-password" name="password" type="password" minLength={10} maxLength={512} autoComplete="new-password" aria-required="true" /></div>
            <div className="form-actions">
              <button type="button" className="secondary" onClick={() => setMode('choose')}>Voltar</button>
              <button className="cta" type="submit" disabled={busy}>{busy ? 'Criando conta…' : 'Criar conta e entrar'}</button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

